import { PlotSystem, beatEffects } from '../systems/PlotSystem';
import { showValueChanges } from './ui';
import { DialogLine } from './VnDialog';
import { FixedEventDef, FixedMedia, PlotHost } from '../data/types';
import { plotLinesFor, mediaBeatOf, isMediaBeat, isChatBeat, isPlayableBeat, plotStepOf, warnUnplayable, formatBeatEffects } from './plotStage';
import { withLineAvatars } from './npcAvatar';
import { prefetchMediaImage } from './mediaPrefetch';

/**
 * 剧情演出档的公共排干器（2026-10-14 茄子令「爆火剧情挪到点击确认行程之后、在决策页展示」时起抽）。
 *
 * 为什么要有这个文件：同一条筛原本在月度执行页（`tryPlayPlot`）和自由出行地图（`playPlotBeat`）
 * 各写了一遍，决策页是第三个宿主——再抄一遍就是三处维护同一条判据，
 * 以后改一处漏两处会出现「决策页演得、执行页演不得」这类分叉（项目纪律：同形状只写一份）。
 *
 * 分工：宿主只提供三张嘴——VN 怎么播（各家底图/常驻态不同）、媒体卡往哪弹、演完走谁的出口；
 * 「取本宿主那一片队头，media 就弹卡、encounter 就走 VN，播出那一刻 `deliver` 结算，再连着演」全在这里。
 * 场地口径（`FixedEventDef.playOn`，缺省执行档）由 `PlotSystem.pendingFor` 一处判。
 */
export interface PlaybackHost {
    /** 播一段台词（宿主自己的 VN 实例与底图/场景态）；picked=选项下标，null=无选项读到尾。
     *  `ev`＝这一拍所属事件：全屏场景宿主按它的 `scene` 换底图，其余宿主用不到。 */
    playLines(lines: DialogLine[], onEnd: (picked: number | null) => void, ev: FixedEventDef): void;
    /** 弹 media 卡（宿主自己的 MediaPanel 实例） */
    showMedia(media: FixedMedia): void;
    /** 本宿主这一片演完了（队里没有可演的拍）——宿主走自己的出口（下周/收场/开月度执行页） */
    onDrained(): void;
}

export class PlotPlayer {
    /** 正在弹的 media 条目 id（onMediaDone 按它推进；演出条目可能不是队头——message 条目让位挂着） */
    private playingEventId: string | null = null;

    constructor(private host: PlotHost, private h: PlaybackHost) { }

    /**
     * 演本宿主队头那一拍。返回 true＝本轮由演出档接手（宿主让位，推进权在播放器回调里）；
     * false＝没演（队空／表内查无），宿主照原路走。
     * message 拍跳过不演（2026-10-14 茄子定：月底 `sweepChatDelivery` 统一发送；跳过≠推进，条目原地等月底），
     * 跳过它们找第一条可演的，不许队头阻塞。
     */
    playNext(): boolean {
        const plot = PlotSystem.getInstance();
        // 播台词期间就把队列里 media 拍的配图 low 优先级预取走，卡打开时命中缓存（幂等，重复进入无害）
        this.prefetchPendingMediaImages(plot);
        const msg = plot.pendingFor(this.host).find(m => {
            const e = plot.eventById(m.eventId);
            return !e || !isChatBeat(e, m);
        });
        if (!msg) return false;
        const ev = plot.eventById(msg.eventId);
        if (!ev) { warnUnplayable(msg.eventId, msg.stepIndex, '表内查无'); return false; }
        if (isMediaBeat(ev, msg)) {
            const media = mediaBeatOf(ev, msg);
            if (!media) {
                warnUnplayable(ev.id, msg.stepIndex, 'media 数据缺失');
                plot.advancePlot(ev.id);
                return this.playNext();
            }
            plot.deliver(ev.id);                             // 交付＝结算闸门（幂等，续拍不会二次发钱）
            this.playingEventId = ev.id;
            this.h.showMedia(media);
            // 媒体卡上没有变动行的位置 ⇒ 这一拍的数值变化用**偏白气泡**承载，**卡打开同时飘**
            // （2026-10-22 用户令「不出对话框也能承载数值变化」＋「卡打开同时飘」）。
            // 2026-11-01 起与普通提示共用同一枚 `#app-hint` 单例（同位置/层级/时长，后到顶替）。
            // ⛔ 对话拍（VN 卡）不飘：那一拍的变动行本来就画在卡上（plotLinesFor 已带 changes）。
            const changes = formatBeatEffects(beatEffects(ev, plotStepOf(ev, msg), msg.scriptId));
            if (changes.length) showValueChanges(changes);            return true;                                     // 推进由 MediaPanel 的 onDone 回调接手
        }
        if (!isPlayableBeat(ev, msg)) {
            const step = plotStepOf(ev, msg);
            warnUnplayable(ev.id, msg.stepIndex, String(step ? step.medium : ev.medium));
            plot.advancePlot(ev.id);                         // 未知载体：跳过这一拍
            return this.playNext();
        }
        plot.deliver(ev.id);
        const lines = plotLinesFor(ev, msg);
        withLineAvatars(lines, () => this.h.playLines(lines, (picked: number | null) => {
            if (picked === null) plot.advancePlot(ev.id);
            else plot.choosePlot(ev.id, picked);
            if (!this.playNext()) this.h.onDrained();        // 一条完了还有下一条就接着演，全演完才走宿主出口
        }, ev));
        return true;
    }

    /** 把本宿主队列里 media 拍的配图交给预取（只决定时机，幂等/失败处理在 mediaPrefetch） */
    private prefetchPendingMediaImages(plot: PlotSystem): void {
        for (const m of plot.pendingFor(this.host)) {
            const ev = plot.eventById(m.eventId);
            if (!ev || !isMediaBeat(ev, m)) continue;
            prefetchMediaImage(mediaBeatOf(ev, m)?.image);
        }
    }

    /** MediaPanel「知道了」回调：推进那条已交付的条目，接着演下一条，或交回宿主出口 */
    /** 本宿主队列里还有几条待演（宿主在入队前用它判"要不要等"） */
    pendingCount(): number {
        return PlotSystem.getInstance().pendingFor(this.host).length;
    }
    onMediaDone(): void {
        const plot = PlotSystem.getInstance();
        const id = this.playingEventId;
        this.playingEventId = null;
        if (id) plot.advancePlot(id);
        if (!this.playNext()) this.h.onDrained();
    }
}
